import { format, isValid, parseISO } from 'date-fns';
import React from 'react';
import { Box, Paper, TextField, Typography } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { BASE_COLORS } from '../../../constants';
import { useDroughtWarning } from './DroughtWarningContext';
import DroughtWarningRainTable from './DroughtWarningRainTable';

const PANEL_SX = {
    backgroundColor: BASE_COLORS.PRIMARY_MAIN,
    border: `1px solid ${BASE_COLORS.PRIMARY_MAIN}`,
    boxShadow: 'none',
    color: BASE_COLORS.WHITE,
};

const getCalendarDate = (selectedDate) => {
    const parsedDate = parseISO(selectedDate);

    return isValid(parsedDate) ? parsedDate : new Date();
};

const formatSummaryNumber = (value, maximumFractionDigits = 1) => {
    const numericValue = Number(value);

    if (value === null || typeof value === 'undefined' || Number.isNaN(numericValue)) {
        return '-';
    }

    return numericValue.toLocaleString('hu-HU', { maximumFractionDigits });
};

const getDroughtStatusText = (isDrought) => {
    if (isDrought === true) {
        return 'Aszályos állapot áll fenn.';
    }

    if (isDrought === false) {
        return 'Nincs aszályos állapot.';
    }

    return 'Az aszály állapota nem meghatározható.';
};

const getSummaryText = (
    formattedSelectedDate,
    droughtInfoRows,
    droughtInfoLoading,
    selectedTargetLabel
) => {
    if (droughtInfoLoading) {
        return 'Aszály adatok betöltése folyamatban van.';
    }

    if (!droughtInfoRows.length) {
        return `${formattedSelectedDate} naphoz nem érkezett aszály adat a kiválasztott ${selectedTargetLabel}.`;
    }

    const [summaryRow] = droughtInfoRows;
    const precipSum = formatSummaryNumber(summaryRow.precip_30d_sum_mm);
    const hotDays = formatSummaryNumber(summaryRow.hot_days_gt_31_count, 0);

    return `A ${formattedSelectedDate} napot megelőző 30 napban ${precipSum} mm csapadék és ${hotDays} db 31°C feletti nap volt. ${getDroughtStatusText(
        summaryRow.is_drought
    )}`;
};

const DroughtWarningSidebar = () => {
    const {
        selectedDate,
        setSelectedDate,
        droughtInfoRows,
        droughtInfoLoading,
        selectedMeparCode,
        selectedGridId,
    } = useDroughtWarning();
    const calendarDate = getCalendarDate(selectedDate);
    const formattedSelectedDate = format(calendarDate, 'yyyy.MM.dd.');
    const hasSelection = Boolean(selectedMeparCode || selectedGridId);
    const selectedTargetLabel = selectedGridId ? 'grid elemre' : 'MePAR blokkra';
    const summaryText = getSummaryText(
        formattedSelectedDate,
        droughtInfoRows,
        droughtInfoLoading,
        selectedTargetLabel
    );

    return (
        <Box
            sx={{
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1, md: 1.5 },
                    mb: { xs: 1.5, md: 2 },
                }}
            >
                <Box
                    sx={{
                        borderRadius: 1,
                        backgroundColor: BASE_COLORS.PRIMARY_MAIN,
                        width: '100%',
                        '& .MuiPickerStaticWrapper-root': {
                            width: '100% !important',
                            borderRadius: 1,
                        },
                        '& .MuiCalendarOrClockPicker-root': {
                            width: '100% !important',
                            borderRadius: 1,
                        },
                        '& .MuiCalendarPicker-root': {
                            width: '100% !important',
                            borderRadius: 1,
                        },
                        '& .MuiPickersCalendarHeader-root': {
                            px: 1,
                            pt: 1,
                        },
                        '& .MuiPickersCalendarHeader-label': {
                            fontSize: { xs: 13, sm: 14 },
                        },
                        '& .MuiDayPicker-header': {
                            px: 1,
                        },
                        '& .MuiDayPicker-weekContainer': {
                            px: 1,
                        },
                    }}
                >
                    <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        openTo="day"
                        views={['day']}
                        disableFuture
                        inputFormat="yyyy-MM-dd"
                        value={calendarDate}
                        onChange={(newValue) => {
                            if (newValue && isValid(newValue)) {
                                setSelectedDate(format(newValue, 'yyyy-MM-dd'));
                            }
                        }}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </Box>
            </Box>

            {hasSelection ? (
                <>
                    <Paper
                        elevation={0}
                        square
                        sx={{
                            ...PANEL_SX,
                            px: 2,
                            py: 1,
                            textAlign: 'center',
                            mb: 0.5,
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'inherit',
                                fontSize: { xs: 14, sm: 15, md: 16 },
                                lineHeight: 1.45,
                            }}
                        >
                            {summaryText}
                        </Typography>
                    </Paper>
                    <DroughtWarningRainTable
                        selectedDate={formattedSelectedDate}
                        rows={droughtInfoRows}
                        loading={droughtInfoLoading}
                    />
                </>
            ) : null}
        </Box>
    );
};

export default DroughtWarningSidebar;
